import { templateManagementApi } from '@/api'
import { addToast } from '@/composables/useToasts'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import useItemNamesFromLocalesFile, { SDS_CANONICAL_PREFIX } from './useItemNamesFromLocalesFile'
import useTreeItemsSection from './useTreeItemsSection'
import { PassportCategory, PassportType, Template, Templates } from '@coac-gmbh/saifty-main-apis'
import {
  SDS_PASSPORT_ID,
  SDS_TREE,
  SDSSection,
  TDS_TREE,
  CUSTOM_PASSPORT_SUBSECTION_SPECIAL_CHARACTER,
  SPECIFICATION_HEADER_SECTION_OBJECT,
  isHardcodedPassport,
  getHardcodedPassportType,
  HARDCODED_PASSPORTS_REGISTRY,
  getPassportStructureById,
} from '@/config'

export function getApprovalsParentSectionListByFieldnames(fieldnames: string[], treeSections: SDSSection[]) {
  const itemsSections = fieldnames.map((fieldname) => fieldname.split('.')[0])

  return treeSections.filter(
    (section) =>
      itemsSections.some((i) => i === section.id || i.startsWith(section.id + '_')) ||
      section.items?.some((item) => itemsSections.some((i) => i.startsWith(item.id)))
  )
}

export function isPassportASubPassportOfTheTDSorSDSPassport(treeSectionsInExtraction: SDSSection[]) {
  const treeSectionsIds = treeSectionsInExtraction.map((i) => i.id)

  const TDSSectionFound = [...TDS_TREE, ...TDS_TREE.flatMap((section) => section.items)].some(
    (section) => section && treeSectionsIds.includes(section.id)
  )
  const SDSSectionFound = [...SDS_TREE, ...SDS_TREE.flatMap((section) => section.items)].some(
    (section) => section && treeSectionsIds.includes(section.id)
  )

  return TDSSectionFound || SDSSectionFound
}

export function getCustomTreeByFieldNames(fieldNames: string[]) {
  let treeToReturn = [] as SDSSection[]

  // Add first level sections
  fieldNames.forEach((item) => {
    const [itemCanonicalPath = ''] = item.split('.')
    const itemCanonicalPathSplit = itemCanonicalPath.split(CUSTOM_PASSPORT_SUBSECTION_SPECIAL_CHARACTER)

    const firstSDSSectionPath = itemCanonicalPathSplit.slice(0, 2).join(CUSTOM_PASSPORT_SUBSECTION_SPECIAL_CHARACTER)

    const firstLevelIndexFound = treeToReturn.findIndex(
      (section) => section.name === itemCanonicalPathSplit[0] || section.id === firstSDSSectionPath
    )

    if (firstLevelIndexFound < 0) {
      const isSDSSection = [SPECIFICATION_HEADER_SECTION_OBJECT, ...SDS_TREE].find(
        (section) => section.id === firstSDSSectionPath
      )

      const newSection = isSDSSection
        ? isSDSSection
        : { id: itemCanonicalPathSplit[0], name: itemCanonicalPathSplit[0], items: [] }
      treeToReturn = [...treeToReturn, newSection]
    }
  })

  // Add second level sections
  fieldNames.forEach((item) => {
    const itemCanonicalNameSplit = item.split('.')
    const itemCanonicalPath = itemCanonicalNameSplit[0]
    const itemCanonicalPathSplit = itemCanonicalPath.split(CUSTOM_PASSPORT_SUBSECTION_SPECIAL_CHARACTER)

    if (itemCanonicalPathSplit.length > 1) {
      treeToReturn.forEach((subsection) => {
        if (subsection.name === itemCanonicalPathSplit[0]) {
          const secondLevelIndexFound = subsection.items?.findIndex(
            (subsection) => subsection.name === itemCanonicalPathSplit[1]
          )

          if (subsection.items && secondLevelIndexFound && secondLevelIndexFound < 0) {
            const newSubsection = {
              id: itemCanonicalPathSplit.join(CUSTOM_PASSPORT_SUBSECTION_SPECIAL_CHARACTER),
              name: itemCanonicalPathSplit[1],
            }

            subsection.items = [...subsection.items, newSubsection]
          }
        }
      })
    }
  })

  return treeToReturn
}

export function getDocumentTreeForPassport(params: {
  passportId: string
  dataObjectFieldNames?: string[]
  templateFieldNames?: string[]
}) {
  const fieldNamesToUse = isHardcodedPassport(params.passportId)
    ? params.dataObjectFieldNames
    : params.templateFieldNames

  return getPassportStructureById(params.passportId).getStructure(fieldNamesToUse)
}

export function getCustomPassportStructureByFieldNames(fieldNames: string[]) {
  const orderedFieldNames =
    fieldNames?.sort(
      (a, b) => Number(!a.startsWith(SDS_CANONICAL_PREFIX)) - Number(!b.startsWith(SDS_CANONICAL_PREFIX))
    ) || []
  const fullTree = getCustomTreeByFieldNames(orderedFieldNames)
  const sections = getApprovalsParentSectionListByFieldnames(orderedFieldNames, fullTree)
  // Sort sections and add index number to avoid duplicates in the UI
  sections.sort((a, b) => (a.index ?? Infinity) - (b.index ?? Infinity))
  let maxIndex = Math.max(0, ...sections.map((s) => s.index ?? 0))
  for (const s of sections) {
    if (s.index == null) {
      maxIndex++
      s.index = maxIndex
    }
  }
  return sections
}

function getPassportTypeByPassportId(passportId: string) {
  return getHardcodedPassportType(passportId)
}

export const ENV_DEFAULT_PASSPORT_ID = window.ENV.DEFAULT_SELECTED_PASSPORT_ID

/* Note, you must manually load the templates by calling loadTemplates() */
export default function usePassportTemplates({
  passportTypes = [PassportCategory.Regular],
  loadPassportsOnMounted = false,
  allowDynamicPassport = false,
} = {}) {
  const { t } = useI18n()
  const { itemNamesFromLocalesFile } = useItemNamesFromLocalesFile()
  const { getPassportItemsBySection, getPassportExactSubSectionItems } = useTreeItemsSection(
    itemNamesFromLocalesFile.value
  )
  const currentPassportItems = ref<string[]>([])
  const selectedTemplate = ref(SDS_PASSPORT_ID)
  const userTemplates = ref<Templates>()
  const currentTemplateData = ref<Template>()
  const loadingTemplate = ref<boolean>(false)
  const loadingUserTemplates = ref<boolean>(false)
  const deletingTemplate = ref<boolean>(false)
  const templateOptions = computed<Array<{ name: string; value: string }>>(() => {
    const templates =
      userTemplates.value?.templates?.map((t) => {
        return { name: t.name || t.id!, value: t.id! } // The ID is mandatory even if the spec doesn't reflect that
      }) || []

    // Build hardcoded options from the registry
    const hardcodedOptions = Object.values(HARDCODED_PASSPORTS_REGISTRY)
      .filter((passport) => {
        // Always show passports without envFlag, otherwise check the flag
        if (!passport.envFlag) return true
        if (passport.isDynamic && !allowDynamicPassport) return false
        return passport.envFlag()
      })
      .map((passport) => ({
        name: passport.isDynamic ? t(passport.name) : passport.name,
        value: passport.id,
      }))

    return [...hardcodedOptions, ...templates]
  })
  const labelsTemplateOptions = computed<Array<{ name: string; value: string }>>(() => {
    return (
      userTemplates.value?.templates
        ?.filter((i) => i.category === 'label')
        .map((t) => {
          return { name: t.name || t.id!, value: t.id! } // The ID is mandatory even if the spec doesn't reflect that
        }) || []
    )
  })
  const passportOptionsWithType = computed<Array<{ name: string; value: string; type: PassportType }>>(() => {
    const passports =
      userTemplates.value?.templates?.map((t) => {
        return { name: t.name || t.id!, value: t.id!, type: t.passportType! } // The ID is mandatory even if the spec doesn't reflect that
      }) || []

    // Build hardcoded options with type from the registry
    const hardcodedOptions = Object.values(HARDCODED_PASSPORTS_REGISTRY)
      .filter((passport) => !passport.isDynamic) // Dynamic passports are not shown in type-based lists
      .map((passport) => ({
        name: passport.name,
        value: passport.id,
        type: passport.type,
      }))

    return [...hardcodedOptions, ...passports]
  })

  onMounted(() => {
    if (loadPassportsOnMounted) loadTemplates()
  })

  watch(templateOptions, (newValues, oldValues) => {
    if (oldValues && oldValues.length === 2 && newValues && newValues.length > oldValues.length) {
      if (isDefaultPassportInPassportOptions() && ENV_DEFAULT_PASSPORT_ID) {
        selectedTemplate.value = ENV_DEFAULT_PASSPORT_ID
      }
    }
  })

  async function loadCurrentTemplate(forced = false) {
    if (
      !isHardcodedPassport(selectedTemplate.value) &&
      (!currentTemplateData.value ||
        (currentTemplateData.value && currentTemplateData.value.id !== selectedTemplate.value) ||
        forced)
    )
      await loadSpecificTemplate(selectedTemplate.value)
  }

  async function loadTemplates() {
    try {
      loadingUserTemplates.value = true
      const { data: templatesData } = await templateManagementApi.getTemplates()
      userTemplates.value = {
        templates: (templatesData.templates || []).filter(
          (passport) => !passport.category || passportTypes.includes(passport.category)
        ),
      }
    } catch (e) {
      addToast({ text: t('Error loading passport templates'), variant: 'danger' })
    }
    loadingUserTemplates.value = false
  }

  async function loadSpecificTemplate(templateId: string) {
    loadingTemplate.value = true
    try {
      const { data } = await templateManagementApi.getTemplate({ templateId })
      currentTemplateData.value = {
        ...data,
        passportType: data.passportType || getPassportTypeByFields(data.fieldNames || []),
      }

      // If you are a developer and you are building a template for testing purposes, you can replace the currentTemplateData.value with a hardcoded template
      // if (currentTemplateData.value) {
      // currentTemplateData.value = <fullLabelTemplateData>
      // }
    } catch (e) {
      addToast({ text: t('Error loading template data'), variant: 'danger' })
    }
    loadingTemplate.value = false
  }

  function getPassportTypeByFields(fieldnames: string[]) {
    if (fieldnames.some((item) => item.startsWith('SEC_'))) return PassportType.Sds
    else if (fieldnames.some((item) => item.startsWith('PRODUCT_'))) return PassportType.Tds
    else return PassportType.Custom
  }

  /* Deletes currently selected template */
  async function deleteTemplate() {
    deletingTemplate.value = true
    try {
      await templateManagementApi.deleteTemplate({ templateId: selectedTemplate.value })
      addToast({ text: t('Passport deleted'), variant: 'success' })
      loadTemplates()
      selectedTemplate.value = SDS_PASSPORT_ID
      currentTemplateData.value = undefined
    } catch {
      addToast({ text: t('Error deleting template'), variant: 'danger' })
    }
    deletingTemplate.value = false
  }

  function selectItemsBySectionId(sectionId: string) {
    const itemOfTheSection = getPassportItemsBySection(sectionId).map((item) => {
      return item.value
    })

    // If some items are selected previously, we dont want to add the same item (avoid duplicate items)
    const itemsNotSelected = itemOfTheSection.filter((itemOfTheSection) => {
      return currentPassportItems.value.every((requestItem) => itemOfTheSection !== requestItem)
    })

    currentPassportItems.value = [...currentPassportItems.value, ...itemsNotSelected]
  }

  function unselectItemsBySectionId(sectionId: string) {
    const itemsSelected = getSelectedItemsBySectionId(sectionId)

    currentPassportItems.value = currentPassportItems.value.filter((requestItem) => {
      return itemsSelected.every((itemSelected) => requestItem !== itemSelected)
    })
  }

  function getSelectedItemsBySectionId(sectionId: string) {
    const itemOfTheSection = getPassportItemsBySection(sectionId).map((item) => {
      return item.value
    })

    // Items of the section selected for the user
    return itemOfTheSection.filter((itemOfTheSection) => {
      return currentPassportItems.value.find((requestItem) => {
        return itemOfTheSection === requestItem
      })
    })
  }

  function handleSelectOrUnselectSectionItems(sectionId: string, isSelectAction: boolean) {
    if (isSelectAction) {
      selectItemsBySectionId(sectionId)
    } else {
      unselectItemsBySectionId(sectionId)
    }
  }

  function allItemsOfTheSectionIsChecked(sectionId: string, allItemsSelectedOfTheUser: string[]) {
    const itemsOfTheSection = getPassportItemsBySection(sectionId).map((item) => {
      return item.value
    })

    const itemsOfTheSectionSelected = allItemsSelectedOfTheUser.filter((item) => {
      const itemName = item.split('.')[0] || ''
      return itemName && (itemName === sectionId || itemName.startsWith(sectionId + '_'))
    })

    return itemsOfTheSection.sort().join(',') === itemsOfTheSectionSelected.sort().join(',')
  }

  function itemsSelectedOfTheSection(sectionId: string, allItemsSelectedFromTheUser: string[]) {
    const itemsOfTheSection = getPassportItemsBySection(sectionId).map((item) => {
      return item.value
    })

    return itemsOfTheSection.filter((itemOfSection) =>
      allItemsSelectedFromTheUser.some((itemOfTheUser) => itemOfSection === itemOfTheUser)
    )
  }

  function getExtraItemsFromPassport(allItemsSelectedFromTheUser: string[]) {
    return allItemsSelectedFromTheUser.filter((item) => !item.startsWith('SEC_') && !item.startsWith('PRODUCT_'))
  }

  function isPassportSharedWithMe(passportId: string) {
    return userTemplates.value?.templates?.find((passport) => passport.id === passportId)?.isShared
  }

  function isDefaultPassportInPassportOptions() {
    return !!(ENV_DEFAULT_PASSPORT_ID && templateOptions.value.find((i) => i.value === ENV_DEFAULT_PASSPORT_ID))
  }

  return {
    ENV_DEFAULT_PASSPORT_ID,
    loadingTemplate,
    loadingUserTemplates,
    deletingTemplate,
    selectedTemplate,
    templateOptions,
    passportOptionsWithType,
    labelsTemplateOptions,
    currentTemplateData,
    currentPassportItems,
    userTemplates,
    loadTemplates,
    loadSpecificTemplate,
    deleteTemplate,
    loadCurrentTemplate,
    handleSelectOrUnselectSectionItems,
    allItemsOfTheSectionIsChecked,
    itemsSelectedOfTheSection,
    getExtraItemsFromPassport,
    getPassportTypeByPassportId,
    isPassportSharedWithMe,
    isDefaultPassportInPassportOptions,
    getDocumentTreeForPassport,
  }
}
